import React from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { axiosget, axiospost } from "../../helpers/httprequest";
import PageTitle from "../../components/PageTitle";
import { Skeleton } from "../../components/ui/Skeleton";
import { Alert, AlertTitle, AlertDescription } from "../../components/ui/Alert";
import { Card } from "../../components/ui/Card";
import { Badge } from "../../components/ui/Badge";
import { Button } from "../../components/ui/Button";
import EmptyState from "../../components/ui/EmptyState";
import is_null from "../../helpers/is_null";
import swal from "../../helpers/swal";
import { motion } from "framer-motion";
import { FiRefreshCw, FiArrowRight } from "react-icons/fi";

const ChangeProgramme = () => {
  const [programme, setprogramme] = React.useState(null);
  const [loading, setloading] = React.useState(false);
  const queryClient = useQueryClient();

  const { data, isLoading, error } = useQuery({
    queryKey: ["myProgrammeList"],
    queryFn: async () => {
      const req = await axiosget(`ms_programme/change`);
      return req?.data;
    },
  });

  const _change = async () => {
    try {
      setloading(true);
      if (is_null(programme)) {
        throw new Error("Please select the programme you want to change to");
      }
      const req = await axiospost("ms_course_change", { to: programme });
      if (req.status !== 200) throw new Error("Something went wrong");
      swal(req?.data?.type, req?.data?.message);
      if (req?.data?.type === "success") queryClient.invalidateQueries({ queryKey: ["myProgrammeList"] });
      setloading(false);
    } catch ({ message }) {
      swal("error", message);
      setloading(false);
    }
  };

  const getStatusInfo = (status) => {
    if (status === 0) return { label: "Pending", variant: "warning" };
    if (status === 1) return { label: "Approved", variant: "success" };
    return { label: "Rejected", variant: "danger" };
  };

  return (
    <div>
      <PageTitle title="Change of Programme" sub="Request a transfer to a different academic programme" />

      {isLoading && (
        <div className="space-y-4 mt-4">
          <Skeleton className="h-16 w-full" />
          <Skeleton className="h-12 w-full" />
        </div>
      )}

      {error && <Alert variant="danger" className="mt-4"><AlertTitle>Error</AlertTitle><AlertDescription>{error?.message || String(error)}</AlertDescription></Alert>}
      {data?.type !== undefined && <Alert variant="danger" className="mt-4"><AlertTitle>Error</AlertTitle><AlertDescription>{data?.message}</AlertDescription></Alert>}

      {data?.type === undefined && (
        <Card className="mt-4">
          <div className="flex flex-col sm:flex-row gap-4 items-end">
            <div className="flex-1 w-full">
              <label className="block text-sm font-medium text-neutral-700 mb-1.5">Select New Programme</label>
              <select
                className="w-full rounded-lg border border-neutral-300 py-2.5 px-4 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-primary-300 focus:border-primary-400 transition-all"
                onChange={(e) => setprogramme(e.target.value)}
                defaultValue=""
              >
                <option value="">Choose a programme...</option>
                {data?.check?.map((v, i) => (
                  <option key={`${v._id}reg${i}`} value={v._id}>
                    {v.code} - {v.name}
                  </option>
                ))}
              </select>
            </div>
            <Button onClick={_change} loading={loading} size="lg" className="w-full sm:w-auto whitespace-nowrap">
              Apply Change
            </Button>
          </div>
        </Card>
      )}

      {/* Change History */}
      {data?.changes?.length === 0 && (
        <EmptyState variant="no-data" title="No Changes Yet" description="You haven't made any programme change requests." className="mt-6" />
      )}

      {data?.changes?.length > 0 && (
        <div className="mt-6">
          <h3 className="text-lg font-bold text-neutral-800 mb-4">Change History</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {data?.changes?.map((v, i) => {
              const status = getStatusInfo(v?.approvalstatus);
              return (
                <motion.div key={i} initial={{ opacity: 0, y: 15 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.05 }}>
                  <Card className="h-full">
                    <div className="flex items-center justify-between mb-4">
                      <div className="w-9 h-9 rounded-xl bg-primary-100 flex items-center justify-center">
                        <FiRefreshCw className="w-4 h-4 text-primary-600" />
                      </div>
                      <Badge variant={status.variant} size="sm" dot>{status.label}</Badge>
                    </div>

                    <div className="space-y-3">
                      <div>
                        <p className="text-xs text-neutral-500 font-medium">From</p>
                        <p className="text-sm font-semibold text-neutral-800">{v?.frominfo?.code} - {v?.frominfo?.name}</p>
                      </div>
                      <div className="flex justify-center"><FiArrowRight className="w-4 h-4 text-neutral-400" /></div>
                      <div>
                        <p className="text-xs text-neutral-500 font-medium">To</p>
                        <p className="text-sm font-semibold text-neutral-800">{v?.toinfo?.code} - {v?.toinfo?.name}</p>
                      </div>
                    </div>

                    <div className="mt-4 pt-4 border-t border-neutral-100 flex justify-between text-xs text-neutral-500">
                      <span>{v?.fromlevel} | {v?.fromsemester === 1 ? "1st Sem" : "2nd Sem"}</span>
                      <span>ID: {v?.oldid}</span>
                    </div>
                  </Card>
                </motion.div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};

export default ChangeProgramme;
